import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react"
import {
  XMarkIcon,
  PaperAirplaneIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  FaceSmileIcon,
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { Fragment } from "react"
import { Button, ScrollShadow, Avatar, Badge, Spinner } from "components/ui"
import { Textarea, Input } from "components/ui/Form"
import { formatTime, formatDate } from "utils/dateUtils"
import { getTemplates, extractTemplateVariables, replaceTemplateVariables, htmlToPlainText } from "services/templateService"
import { sendSmsToLead, sendSmsToMultipleLeads } from "services/leadsService"
import { SMSServiceSelector } from "components/shared/SMSServiceSelector"
import { toast } from "sonner"

// Mobile number validation utility
const isValidMobile = (mobile) => {
  const mobileRegex = /^[+]?[1-9][\d]{0,15}$/
  return mobileRegex.test(mobile.replace(/\s+/g, ''))
}

// Mobile Chips Input Component (non-editable, backend handles phone numbers)
const MobileChipsInput = ({ label, mobiles, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState("")
  const [inputError, setInputError] = useState("")

  const addMobile = (mobile) => {
    const trimmedMobile = mobile.trim()
    if (!trimmedMobile) return

    if (!isValidMobile(trimmedMobile)) {
      setInputError("Please enter a valid mobile number")
      return
    }

    if (mobiles.some(m => m.number === trimmedMobile)) {
      setInputError("Mobile number already added")
      return
    }

    onChange([...mobiles, { 
      number: trimmedMobile, 
      name: `Contact ${mobiles.length + 1}`,
      id: `temp_${Date.now()}_${mobiles.length}`,
      hasPhone: true
    }])
    setInputValue("")
    setInputError("")
  }

  const removeMobile = (index) => {
    onChange(mobiles.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addMobile(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && mobiles.length > 0) {
      // Remove last mobile when backspace is pressed on empty input
      removeMobile(mobiles.length - 1)
    }
  }

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addMobile(inputValue)
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      
      <div className={`min-h-[42px] p-2 border rounded-lg bg-white dark:bg-gray-800 transition-colors ${
        inputError 
          ? 'border-red-300 dark:border-red-600 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
          : 'border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'
      }`}>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Mobile Chips */}
          {mobiles.map((mobile, index) => (
            <div
              key={`${mobile.id || mobile.number}-${index}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-700 transition-all duration-200 animate-in fade-in slide-in-from-left-2"
            >
              <DevicePhoneMobileIcon className="w-3.5 h-3.5 opacity-70 shrink-0" />
              <div className="flex flex-col">
                <span className="max-w-[150px] truncate font-medium">{mobile.name}</span>
                <span className="max-w-[150px] truncate text-xs opacity-80">
                  {mobile.number || "xxxxxxxxxx"}
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  removeMobile(index)
                }}
                className="ml-0.5 text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer shrink-0"
                title="Remove this recipient"
                style={{ 
                  pointerEvents: 'auto',
                  minWidth: '24px',
                  minHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <XMarkIcon className="w-4 h-4" style={{ pointerEvents: 'none' }} />
              </button>
            </div>
          ))}
          
          {/* Input Field */}
          <input
            type="tel"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setInputError("")
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder={mobiles.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>
      
      {inputError && (
        <p className="text-sm text-red-600 dark:text-red-400">{inputError}</p>
      )}
      
      {mobiles.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {mobiles.length} recipient{mobiles.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}

const SMSMessageModal = ({ isOpen, onClose, leadData, selectedLeads = [], isBulkMode = false }) => {
  const [message, setMessage] = useState("")
  const [selectedTab, setSelectedTab] = useState("compose") // "compose" or "template"
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [templateVariables, setTemplateVariables] = useState({})
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateSearch, setTemplateSearch] = useState("")
  const [isSending, setIsSending] = useState(false)
  
  // SMS service selection states
  const [selectedSmsService, setSelectedSmsService] = useState(null)
  const [showSmsServiceSelector, setShowSmsServiceSelector] = useState(false)
  
  // SMS form data
  const [smsData, setSmsData] = useState({
    to: "",
    toMobiles: [], // For bulk mode - array of mobile objects with name and number
    message: ""
  })
  
  const inputRef = useRef(null)
  const templatesLoadedRef = useRef(false)

  // Memoize valid leads to prevent infinite re-renders
  // Include ALL leads, even those without phone numbers
  const validLeads = useMemo(() => {
    return isBulkMode ? selectedLeads : []
  }, [isBulkMode, selectedLeads])

  // Load templates function
  const loadTemplates = useCallback(async (searchTerm = "") => {
    try {
      setTemplatesLoading(true)
      const result = await getTemplates({
        pageNumber: 1,
        pageSize: 50,
        channelId: 2, // SMS channel ID (based on your API data, ChannelId: 2 is SMS)
        search: searchTerm
      })
      if (result.success) {
        setTemplates(result.data)
      } else {
        toast.error(result.error || "Failed to load templates")
      }
    } catch (error) {
      console.error("Error loading templates:", error)
      toast.error("Failed to load templates")
    } finally {
      setTemplatesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
       // Only load templates if not already loaded or modal was closed and reopened
       if (!templatesLoadedRef.current) {
         loadTemplates()
         templatesLoadedRef.current = true
       }
      inputRef.current?.focus()
     } else {
       // Reset templates loaded flag when modal closes
       templatesLoadedRef.current = false
    }
  }, [isOpen, loadTemplates])

   // Separate useEffect for form initialization to avoid infinite loops
   useEffect(() => {
     if (isOpen) {
       // Reset form when modal opens
      const initialToValue = isBulkMode 
        ? `${validLeads.length} recipients selected`
        : (leadData?.mobile || leadData?.Mobile || leadData?.phone || leadData?.PhoneNumber || "")
      
      const initialToMobiles = isBulkMode 
        ? validLeads.map(lead => {
            const mobile = lead.mobile || lead.Mobile || lead.phone || lead.PhoneNumber
            const hasValidMobile = mobile && mobile !== "N/A" && mobile.trim() !== ""
            
            return {
              id: lead.id || lead.leadId || lead.LeadId || lead.userId,
              number: hasValidMobile ? mobile : "",
              name: lead.name || lead.leadname || lead.leadName || lead.Name || 'Unknown Contact',
              hasPhone: hasValidMobile
            }
          })
        : []
 
       setSmsData({
        to: initialToValue,
        toMobiles: initialToMobiles,
        message: ""
      })
      setSelectedSmsService(null) // Reset service selection every time modal opens
     }
   }, [isOpen, isBulkMode, validLeads, leadData])

  // Replace variables only if they have values, otherwise keep the placeholder
  const replaceVariablesSelectively = useCallback((text, variables) => {
    if (!text) return text
    
    let result = text
    Object.keys(variables).forEach(varName => {
      const value = variables[varName]
      // Only replace if value is not empty
      if (value && value.trim() !== '') {
        const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'gi')
        result = result.replace(regex, value)
      }
      // If value is empty, keep the {{variable}} placeholder as-is
    })
    
    return result
  }, [])

  useEffect(() => {
    if (selectedTemplate) {
      const variables = extractTemplateVariables(selectedTemplate.Body)
      const variableDefaults = {}
      variables.forEach(variable => {
        // Set default values based on lead data
        switch (variable.toLowerCase()) {
          case 'name':
          case 'leadname':
            variableDefaults[variable] = leadData?.name || ''
            break
          case 'email':
            variableDefaults[variable] = leadData?.email || ''
            break
          case 'mobile':
          case 'phone':
            variableDefaults[variable] = leadData?.mobile || leadData?.phone || ''
            break
          case 'company':
            variableDefaults[variable] = leadData?.company || ''
            break
          default:
            variableDefaults[variable] = ''
        }
      })
      setTemplateVariables(variableDefaults)
      
      // Set the message to the processed template (only replace variables with values)
      const processedTemplate = htmlToPlainText(replaceVariablesSelectively(selectedTemplate.Body, variableDefaults))
      setMessage(processedTemplate)
      setSmsData(prev => ({
        ...prev,
        message: processedTemplate
      }))
    }
  }, [selectedTemplate, leadData, replaceVariablesSelectively])

  const handleTemplateSearch = (searchTerm) => {
    setTemplateSearch(searchTerm)
    // Clear existing timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout)
    }
    // Set new timeout
    window.searchTimeout = setTimeout(() => {
      loadTemplates(searchTerm)
      templatesLoadedRef.current = true // Mark as loaded after search
    }, 300)
  }

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setSelectedTab("compose") // Switch to compose tab after selecting template
  }

  const handleVariableChange = (variable, value) => {
    const updatedVariables = { ...templateVariables, [variable]: value }
    setTemplateVariables(updatedVariables)
    
    if (selectedTemplate) {
      const processedTemplate = htmlToPlainText(replaceVariablesSelectively(selectedTemplate.Body, updatedVariables))
      setMessage(processedTemplate)
      setSmsData(prev => ({
        ...prev,
        message: processedTemplate
      }))
    }
  }

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message")
      return
    }

    if (!selectedSmsService) {
      // Open SMS service selector modal
      setShowSmsServiceSelector(true)
      return
    }

    // Proceed with sending SMS
    await proceedWithSmsSend(selectedSmsService)
  }

  const proceedWithSmsSend = async (smsService) => {

    if (isBulkMode) {
      // Bulk SMS mode
      if (smsData.toMobiles.length === 0) {
        toast.error("No mobile numbers selected for SMS")
        return
      }

      try {
        setIsSending(true)

        // Transform all mobile chips to lead format for API
        // Backend will handle fetching phone numbers from DB
        const leadsForSms = smsData.toMobiles.map((mobile, index) => {
          // Try to find the corresponding lead data
          const leadForMobile = validLeads.find(lead => 
            (lead.mobile || lead.Mobile || lead.phone) === mobile.number
          )
          
          return {
            id: mobile.id || (leadForMobile ? (leadForMobile.id || leadForMobile.leadId || leadForMobile.LeadId) : `temp_${index}`),
            mobile: mobile.number || null, // Send null if no number, backend will fetch it
            name: mobile.name
          }
        })

        const result = await sendSmsToMultipleLeads(leadsForSms, message, smsService?.ProviderCode)

        if (result.success) {
          toast.success(result.message || `SMS sent to ${smsData.toMobiles.length} leads successfully!`)
    setMessage("")
          setSelectedTemplate(null)
          setTemplateVariables({})
          setSmsData({
            to: "",
            toMobiles: [],
            message: ""
          })
          onClose()
        } else {
          throw new Error(result.message || "Failed to send SMS messages")
        }
      } catch (error) {
        console.error("Error sending bulk SMS:", error)
        toast.error(error.message || "Failed to send SMS messages")
      } finally {
        setIsSending(false)
      }
    } else {
      // Single SMS mode
      if (!leadData?.mobile && !leadData?.Mobile && !leadData?.phone) {
        toast.error("Lead mobile number is required to send SMS")
        return
      }

      if (!leadData?.id && !leadData?.leadId && !leadData?.LeadId) {
        toast.error("Lead ID is required to send SMS")
        return
      }

      try {
        setIsSending(true)

        const leadId = leadData.id || leadData.leadId || leadData.LeadId
        const mobile = leadData.mobile || leadData.Mobile || leadData.phone

        const result = await sendSmsToLead(leadId, mobile, message, smsService?.ProviderCode)

        if (result.success) {
          toast.success(result.message || "SMS sent successfully!")
          setMessage("")
          setSelectedTemplate(null)
          setTemplateVariables({})
          setSmsData({
            to: "",
            toMobiles: [],
            message: ""
          })
          onClose()
        } else {
          throw new Error(result.message || "Failed to send SMS")
        }
      } catch (error) {
        console.error("Error sending SMS:", error)
        toast.error(error.message || "Failed to send SMS")
      } finally {
        setIsSending(false)
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const getMessageStatus = (msg) => {
    if (!msg.sent) return null
    if (msg.read) return "✓✓" // Double check blue
    if (msg.delivered) return "✓✓" // Double check gray
    return "✓" // Single check
  }

  const formatMessageTime = (timestamp) => {
    const now = new Date()
    const msgDate = new Date(timestamp)

    if (now.toDateString() === msgDate.toDateString()) {
      return formatTime(timestamp)
    }
    return formatDate(timestamp)
  }

  const getCharacterCount = () => {
    const count = message.length
    const maxChars = 160
    const smsCount = Math.ceil(count / maxChars) || 1
    return { count, maxChars, smsCount }
  }

  return (
    <>
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClose={()=>{}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all dark:bg-gray-900 outline-none focus:outline-none">
            {/* Header */}
            <div className="bg-[#007AFF] dark:bg-[#1c1c1e] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10 bg-blue-100 text-blue-700 text-sm font-medium">
                    <DevicePhoneMobileIcon className="w-6 h-6" />
                  </Avatar>
                <div>
                  <DialogTitle as="h3" className="text-base font-medium text-white">
                    {isBulkMode 
                      ? `Send SMS to ${selectedLeads.length} Lead${selectedLeads.length > 1 ? 's' : ''}`
                      : `Send SMS to ${leadData?.name || "Lead Contact"}`
                    }
                  </DialogTitle>
                  <p className="text-sm text-white/80">
                    {isBulkMode 
                      ? `${selectedLeads.length} lead${selectedLeads.length > 1 ? 's' : ''} selected for SMS`
                      : (leadData?.mobile || leadData?.Mobile || leadData?.phone || "+91 98765 43210")
                    }
                  </p>
                </div>
              </div>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="w-10 h-10 rounded-full text-white hover:bg-white/10 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </Button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-600">
              <div className="flex px-4">
                <button
                  onClick={() => setSelectedTab("compose")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    selectedTab === "compose"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  Compose Message
                </button>
                <button
                  onClick={() => setSelectedTab("template")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    selectedTab === "template"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  Choose Template
                </button>
              </div>
                          </div>
                          
            {/* Tab Content */}
            <div className="p-6 max-h-[50vh] overflow-y-auto custom-scrollbar">
              {selectedTab === "compose" && (
                <div className="space-y-4">
                  {/* Recipients Section */}
                  {isBulkMode ? (
                    <MobileChipsInput
                      label="Recipients"
                      mobiles={smsData.toMobiles}
                       onChange={(newMobiles) => setSmsData(prev => ({ ...prev, toMobiles: newMobiles }))}
                      placeholder="Add mobile number..."
                    />
                  ) : (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        To
                      </label>
                      <div className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                        <div className="flex items-center gap-2">
                          <DevicePhoneMobileIcon className="w-4 h-4 text-gray-500" />
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {leadData?.name || leadData?.leadname || leadData?.leadName || leadData?.Name || 'Unknown Contact'}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {leadData?.mobile || leadData?.Mobile || leadData?.phone || leadData?.PhoneNumber || 'No mobile number'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedTemplate && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Using Template: {selectedTemplate.Name}
                    </span>
                <Button
                          onClick={() => {
                            setSelectedTemplate(null)
                            setMessage("")
                            setTemplateVariables({})
                          }}
                variant="flat"
                          size="sm"
                          className="ml-auto text-xs"
              >
                          Clear Template
              </Button>
            </div>

                      {/* Template Variables */}
                      {extractTemplateVariables(selectedTemplate.Body).length > 0 && (
                        <div className="space-y-3">
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            Fill in the template variables:
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            {extractTemplateVariables(selectedTemplate.Body).map((variable) => (
                              <div key={variable}>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {variable}
                </label>
                                <Input
                                  value={templateVariables[variable] || ''}
                                  onChange={(e) => handleVariableChange(variable, e.target.value)}
                                  placeholder={`Enter ${variable}`}
                                  className="text-sm"
                />
              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message
                </label>
                <Textarea
                      ref={inputRef}
                      value={message}
                      onChange={(e) => {
                        setMessage(e.target.value)
                        setSmsData(prev => ({ ...prev, message: e.target.value }))
                      }}
                  placeholder="Type your message..."
                      className="min-h-[150px] resize-none"
                      rows={6}
                />
                
                    {/* Character Count */}
                <div className="flex justify-between items-center mt-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    <div>{getCharacterCount().count}/160 characters • {getCharacterCount().smsCount} SMS</div>
                    {isBulkMode && smsData.toMobiles.length > 0 && (
                      <div className="text-xs">
                        {smsData.toMobiles.length} recipient{smsData.toMobiles.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  {getCharacterCount().count > 160 && (
                    <Badge variant="soft" color="warning" className="text-xs">
                      Long Message
                    </Badge>
                  )}
                </div>
              </div>
            </div>
              )}

              {selectedTab === "template" && (
                <div className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={templateSearch}
                      onChange={(e) => handleTemplateSearch(e.target.value)}
                      placeholder="Search templates..."
                      className="pl-10"
                    />
                  </div>

                  {/* Templates List */}
                  {templatesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner className="h-6 w-6" />
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading templates...</span>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                      {templates.length === 0 ? (
                        <div className="text-center py-8">
                          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            No templates found
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            {templateSearch ? "Try a different search term" : "No SMS templates available"}
                          </p>
                        </div>
                      ) : (
                        templates.map((template) => (
                          <div
                            key={template.TemplateId}
                            onClick={() => handleTemplateSelect(template)}
                            className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-500 hover:bg-blue-50 transition-all dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-400 dark:hover:bg-blue-900/20"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                  {template.Name}
                                </h4>
                                {template.Subject && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {template.Subject}
                                  </p>
                                )}
                                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                                  {htmlToPlainText(template.Body)}
                                </p>
                                
                                {extractTemplateVariables(template.Body).length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {extractTemplateVariables(template.Body).map((variable) => (
                                      <Badge key={variable} variant="soft" color="primary" className="text-xs">
                                        {variable}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleTemplateSelect(template)
                                }}
                                variant="outlined"
                                size="sm"
                                className="ml-3"
                              >
                                Use Template
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-600">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {isSending && (
                  <div className="flex items-center gap-2">
                    <Spinner className="w-4 h-4" />
                    Sending SMS...
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  disabled={isSending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={!message.trim() || isSending}
                  className="bg-[#007AFF] hover:bg-[#0056CC]"
                >
                  {isSending ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                      Send SMS
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>

      {/* SMS Service Selector Modal */}
      <SMSServiceSelector
        isOpen={showSmsServiceSelector}
        onClose={() => setShowSmsServiceSelector(false)}
        onSelect={(service) => {
          setSelectedSmsService(service)
          // Automatically proceed with sending after service selection
          setTimeout(() => {
            proceedWithSmsSend(service)
          }, 100)
        }}
        selectedService={selectedSmsService}
        title="Select SMS Service"
        description="Choose which SMS service to use for sending this message"
      />
    </>
  )
}

export default SMSMessageModal
